import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import SettingsValidator from '../entities/settingsValidators';
import { useTranslation } from 'react-i18next';
import SettingsSection from '../entities/configuration/settingsSection';

type Props = {};

const settingsValidator = new SettingsValidator();
const NewUserAppInstance = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [userAppDefs, setUserAppDefs] = useState<any[]>([]);
  const [userAppDef, setUserAppDef] = useState<any>();
  const [userAppDefId, setUserAppDefId] = useState<any>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [userAppInstance, setUserAppInstance] = useState<any>();

  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const getUserAppDefs = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserAppDefs();
        setUserAppDefs(response);

        const queryDefId = searchParams.get('defId');
        if (queryDefId && response.some((uad: any) => uad.id === queryDefId)) {
          setUserAppDefId(queryDefId);
          setUserAppDef(response.find((uad: any) => uad.id === queryDefId));
          const instance = await apiContext.getNewUserAppInstance(queryDefId);
          setUserAppInstance(instance);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    getUserAppDefs();
  }, [searchParams]);

  const onDefChange = async (userAppDefId: string) => {
    setUserAppDefId(userAppDefId);
    setUserAppDef(userAppDefs.find((dsd) => dsd.id === userAppDefId));
    if (userAppDefId) {
      const userAppInstance = await apiContext.getNewUserAppInstance(userAppDefId);
      setUserAppInstance(userAppInstance);
    } else {
      setUserAppInstance(null);
    }
  };

  const onSettingChange = (settingName: string, value: any) => {
    setUserAppInstance({ ...userAppInstance, settings: { ...userAppInstance.settings, [settingName]: value } });
  };

  const submitEntity = async () => {
    const errors = {} as any;
    let hasError = false;

    if (!userAppDefId) {
      errors['userAppDefId'] = 'Required';
      hasError = true;
    }
    if (!name) {
      errors['name'] = 'Required';
      hasError = true;
    }

    const [settingErrors = {}, hasSettingsError = false] = settingsValidator.validateSettings(
      userAppDef.settingDefs,
      userAppInstance.settings,
      t,
    );

    hasError = hasError || hasSettingsError;
    setErrors({ ...errors, ...settingErrors });
    setHasErrors(hasError);

    if (!hasError) {
      const { id, tenantId, entityType, entityDefId, settings = {} } = userAppInstance;
      try {
        setLoading(true);
        const response = await apiContext.createUserAppInstance({
          id,
          tenantId,
          entityType,
          entityDefId,
          name,
          description,
          settings,
        });
        navigate(`/userAppInstance/${userAppDefId}/${response.id}`, {
          state: { from: '/instances?tab=userAppInstances' },
        });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const close = () => {
    navigate('/instances?tab=userAppInstances');
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          New User App
        </Typography>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SelectField
              label="User app type"
              required
              options={userAppDefs.map((ad) => {
                return {
                  value: ad.id,
                  label: ad.name,
                };
              })}
              value={userAppDefId}
              autoFocus
              onChange={onDefChange}
              error={errors.dataSourceDefId}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <TextField label="Name" value={name} required error={errors.name} onChange={setName} type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <TextField
              label={'Description'}
              value={description}
              multiline
              maxRows={2}
              error={errors.description}
              onChange={setDescription}
            />
          </Grid>
        </Grid>

        {userAppInstance && userAppDef && (
          <Box sx={{ mt: 2 }}>
            {userAppDef?.settingDefs
              .filter((section: any) => !section.system)
              .map((section: any) => (
                <SettingsSection
                  key={section.sectionName}
                  entityDefId={userAppDef.id}
                  settings={userAppInstance.settings}
                  settingsSection={section}
                  errors={errors}
                  onChange={onSettingChange}
                />
              ))}
            {hasErrors && (
              <Box sx={{ mt: 1 }}>
                <ErrorMessage error={{ message: 'Please enter valid values in the fields with error.' }} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button size="small" variant="text" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          loading={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={submitEntity}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default NewUserAppInstance;
